import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Book, Eye, TrendingUp } from 'lucide-react';
import { useNovelStore, useGenreStore } from '../../hooks/useStore';
import { novelAPI } from '../../services/api';
import NovelCard from '../../components/NovelCard/NovelCard';
import AdDisplay from '../../components/AdDisplay/AdDisplay';
import './Home.css';

const Home = () => {
  const { 
    hotNovels, 
    featuredNovels, 
    recentNovels,
    fetchHotNovels, 
    fetchFeaturedNovels, 
    fetchRecentNovels
  } = useNovelStore();
  const { fetchGenres } = useGenreStore();
  const [activeGenre] = useState('all');
  const [completedNovels, setCompletedNovels] = useState([]);

  useEffect(() => {
    fetchHotNovels(12);
    fetchFeaturedNovels();
    fetchRecentNovels(20);
    fetchGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    
    // Fetch completed novels
    const fetchCompleted = async () => {
      try {
        const res = await novelAPI.getAll({ status: 'completed', limit: 5, sort: '-stats.views' });
        setCompletedNovels(res.data || []);
      } catch (err) {
        console.error('Failed to fetch completed novels');
      }
    };
    fetchCompleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredNovels = activeGenre === 'all' 
    ? recentNovels 
    : recentNovels.filter(novel => 
        novel.genres?.some(g => g.slug === activeGenre || g._id === activeGenre)
      );

  const featuredNovel = featuredNovels[0];

  return (
    <main className="home-page">
      {/* Background Pattern */}
      <div className="bg-pattern"></div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Discover the world of <span className="text-gradient">captivating novels</span>
          </h1>
          <p className="hero-subtitle">
            Thousands of hot novels updated continuously. Read for free, unlimited, anytime anywhere.
          </p>

          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Novels</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2M+</div>
              <div className="stat-label">Chapters</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">500K+</div>
              <div className="stat-label">Readers</div>
            </div>
          </div>
        </div>

        {featuredNovel && (
          <div className="hero-featured">
            <Link to={`/novel/${featuredNovel.slug}`} className="featured-card">
              <span className="featured-badge">
                <Star size={14} fill="currentColor" />
                Trending
              </span>
              <h3 className="featured-title">{featuredNovel.title}</h3>
              <p className="featured-desc">{featuredNovel.description}</p>
              <div className="featured-meta">
                <span>
                  <Book size={14} />
                  {featuredNovel.chapterCount} chapters
                </span>
                <span>
                  <Eye size={14} />
                  {formatViews(featuredNovel.stats?.views)} views
                </span>
              </div>
            </Link>
          </div>
        )}
      </section>

      {/* Ad - Home Banner (after hero) */}
      <AdDisplay position="home_banner" className="home-banner-ad" />

      {/* Hot Novels */}
      {hotNovels.length > 0 && (
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">
              <TrendingUp size={24} className="title-icon" />
              Hot Novels
            </h2>
            <Link to="/novels?sort=popular" className="view-all">
              View All
              <ChevronRight size={16} />
            </Link>
          </div>
          <div className="novels-grid">
            {hotNovels.slice(0, 12).map((novel) => (
              <NovelCard key={novel._id} novel={novel} />
            ))}
          </div>
        </section>
      )}

      {/* Ad - Home Middle */}
      <AdDisplay position="home_middle" className="home-middle-ad" />

      {/* Recent Updates */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Latest Updates</h2>
          <Link to="/novels?sort=newest" className="view-all">
            View All
            <ChevronRight size={16} />
          </Link>
        </div>
        <div className="novels-grid">
          {filteredNovels.slice(0, 12).map((novel) => (
            <NovelCard key={novel._id} novel={novel} />
          ))}
        </div>
      </section>

      {/* Ad - Home Bottom */}
      <AdDisplay position="home_bottom" className="home-bottom-ad" />

      {/* Rankings Sidebar */}
      <section className="section rankings-section">
        <div className="rankings-grid">
          <div className="ranking-card">
            <h3 className="ranking-title">🔥 Weekly Top</h3>
            <div className="ranking-list">
              {hotNovels.slice(0, 5).map((novel, index) => (
                <Link 
                  key={novel._id} 
                  to={`/novel/${novel.slug}`}
                  className="ranking-item"
                >
                  <span className={`rank-number rank-${index + 1}`}>
                    {index + 1}
                  </span>
                  <div className="rank-info">
                    <span className="rank-title">{novel.title}</span>
                    <span className="rank-meta">
                      <Eye size={12} />
                      {formatViews(novel.stats?.views)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="ranking-card">
            <h3 className="ranking-title">⭐ Top Rated</h3>
            <div className="ranking-list">
              {[...recentNovels]
                .sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0))
                .slice(0, 5)
                .map((novel, index) => (
                  <Link 
                    key={novel._id} 
                    to={`/novel/${novel.slug}`}
                    className="ranking-item"
                  >
                    <span className={`rank-number rank-${index + 1}`}>
                      {index + 1}
                    </span>
                    <div className="rank-info">
                      <span className="rank-title">{novel.title}</span>
                      <span className="rank-meta">
                        <Star size={12} fill="currentColor" />
                        {novel.rating?.average || 0}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          </div>

          <div className="ranking-card">
            <h3 className="ranking-title">📚 Completed</h3>
            <div className="ranking-list">
              {completedNovels.length > 0 ? (
                completedNovels.slice(0, 5).map((novel, index) => (
                  <Link 
                    key={novel._id} 
                    to={`/novel/${novel.slug}`}
                    className="ranking-item"
                  >
                    <span className={`rank-number rank-${index + 1}`}>
                      {index + 1}
                    </span>
                    <div className="rank-info">
                      <span className="rank-title">{novel.title}</span>
                      <span className="rank-meta">
                        <Book size={12} />
                        {novel.chapterCount} chapters
                      </span>
                    </div>
                  </Link>
                ))
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', padding: '10px 0' }}>
                  No completed novels yet
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Helper function
const formatViews = (views) => {
  if (!views) return '0';
  if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M';
  if (views >= 1000) return (views / 1000).toFixed(1) + 'K';
  return views.toString();
};

export default Home;